import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import {
    faPlus, faTrash, faPencil, faChevronDown, faChevronRight,
    faMagnifyingGlass, faFlask, faCircleCheck, faCircleXmark,
    faLock, faTriangleExclamation, faCircleInfo, faArrowRight, faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Icon from "../../common/elements/Icon";
import Button from "../../common/elements/Button";
import StateHandler from "../../common/elements/StateHandler";
import SmallLoadingSpinner from "../../common/elements/SmallLoadingSpinner";
import ConfirmDialog from "./ConfirmDialog";
import useFetchWithMsal from "../../../useFetchWithMsal";
import { thor_api_urls } from "../../common/Constants";
import { buildDataState, buildErrorState, buildLoadingState } from "../../../utils/StateUtils";
import { serverErrMsg } from "../utils/validators";
import { useThor } from "../context/ThorContext";
import {
    OPERATION_PHRASE, MATCH_TYPE_OPTIONS, RANGE_OPERATIONS,
    describeAudience, valueCountIssue, audienceMatches,
} from "../utils/audienceLanguage";

const isNumericAttr = (allowOperation) => allowOperation === true;

// ── Tiny presentational helpers ─────────────────────────────────────────────

const ValueChip = ({ label, onRemove, removing, isNew, tone = 'default' }) => {
    const palette = tone === 'new'
        ? { background: '#eef9f3', border: '1px solid #cdeede' }
        : { background: '#f1f4fb', border: '1px solid #d8e0f2' };
    return (
        <span className="tag is-medium mr-1 mb-1" style={{ ...palette, maxWidth: 240, fontFamily: 'var(--font-mono, monospace)', fontSize: '0.8rem' }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
            {isNew && <span className="has-text-success ml-1" style={{ fontSize: '0.65rem', fontFamily: 'inherit' }}>new</span>}
            {onRemove && <button className="delete is-small ml-1" disabled={removing} onClick={onRemove} aria-label={`Remove ${label}`} />}
        </span>
    );
};

// ── Add-a-single-value modal (row-level: attribute catalog + audience values) ─

const AddValueModal = ({ isActive, title, selectOptions, loadingOptions, onClose, onSubmit, isSubmitting, freeText, placeholder }) => {
    const [value, setValue]     = useState('');
    const [selected, setSelected] = useState(null);
    const canSubmit = freeText ? value.trim() : !!selected;

    const wasActive = useRef(false);
    if (isActive && !wasActive.current) {
        wasActive.current = true;
        setTimeout(() => { setValue(''); setSelected(null); }, 0);
    }
    if (!isActive) wasActive.current = false;

    return (
        <div className={`modal ${isActive ? 'is-active' : ''}`}>
            <div className="modal-background" onClick={onClose} />
            <div className="modal-card" style={{ maxWidth: 440 }}>
                <header className="modal-card-head">
                    <p className="modal-card-title">{title}</p>
                    <button className="delete" onClick={onClose} />
                </header>
                <section className="modal-card-body">
                    {freeText ? (
                        <div className="field">
                            <label className="label">Value <span className="has-text-danger">*</span></label>
                            <div className="control">
                                <input className="input" value={value}
                                    onChange={e => setValue(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && canSubmit && onSubmit(value)}
                                    placeholder={placeholder ?? 'e.g. en_US, mobile'} autoFocus />
                            </div>
                        </div>
                    ) : loadingOptions ? (
                        <SmallLoadingSpinner />
                    ) : selectOptions?.length === 0 ? (
                        <p className="has-text-warning">No known values yet — add values to the attribute first.</p>
                    ) : (
                        <div className="field">
                            <label className="label">Value <span className="has-text-danger">*</span></label>
                            <Select options={selectOptions} value={selected} onChange={setSelected}
                                placeholder="Select a value…" isSearchable autoFocus
                                menuPortalTarget={document.body} menuPosition="fixed"
                                styles={{ menuPortal: b => ({ ...b, zIndex: 9999 }) }} />
                        </div>
                    )}
                </section>
                <footer className="modal-card-foot">
                    <Button className="is-link" disabled={isSubmitting || !canSubmit}
                        onClick={() => onSubmit(freeText ? value : selected?.value)}>
                        {isSubmitting ? 'Adding…' : 'Add'}
                    </Button>
                    <Button onClick={onClose}>Cancel</Button>
                </footer>
            </div>
        </div>
    );
};

// ── Value picker used inside the builder ─────────────────────────────────────
// One searchable control that scales to large catalogs: type to filter known
// values, pick any number, or type a brand-new value to add it. Selections show
// as tags inside the control — no inline wall of every known value.

const ValuePicker = ({ catalog, value, onChange, loading }) => {
    const options = catalog.map(c => ({ value: c.value, label: c.value }));
    const selected = value.map(v => ({ value: v.value, label: v.value }));

    const handleChange = (opts) => {
        onChange((opts ?? []).map(o => {
            const inCat = catalog.find(c => c.value === o.value);
            return { value: o.value, attributeValueId: inCat?.attributeValueId ?? null, isNew: !inCat };
        }));
    };

    return (
        <CreatableSelect
            isMulti isClearable={false}
            options={options}
            value={selected}
            onChange={handleChange}
            isLoading={loading}
            placeholder={catalog.length ? 'Search known values, or type a new one…' : 'Type a value and press enter…'}
            formatCreateLabel={input => `Add “${input}”`}
            noOptionsMessage={() => catalog.length ? 'No match — type to add a new value' : 'Type a value to add'}
            menuPortalTarget={document.body} menuPosition="fixed"
            styles={{ menuPortal: b => ({ ...b, zIndex: 9999 }) }} />
    );
};

// ── Unified audience builder (create + edit, with inline property creation) ───

const NEW_PROPERTY = '__new__';

const AudienceBuilderModal = ({ isActive, initial, attributes, preselectedAttrId, execute, onClose, onSubmit, isSubmitting, onManageProperty }) => {
    const [name, setName]               = useState('');
    const [propertyChoice, setPropertyChoice] = useState(null); // react-select option or {value:NEW_PROPERTY}
    const [newAttrName, setNewAttrName] = useState('');
    const [newAttrNumeric, setNewAttrNumeric] = useState(false);
    const [operation, setOperation]     = useState('IN');
    const [values, setValues]           = useState([]); // [{value, attributeValueId|null, isNew}]
    const [originalValueIds, setOriginalValueIds] = useState([]);
    const [catalog, setCatalog]         = useState([]);
    const [loadingCatalog, setLoadingCatalog] = useState(false);

    const isEdit = !!initial;
    const attrOptions = (attributes ?? []).map(a => ({
        value: a.attributeId, label: a.attributeName, allowOperation: a.allowOperation,
    }));

    // Initialise form state when the modal opens. On edit, the current values are
    // loaded separately by the prefill effect below — don't clear them here (a
    // clear racing the async prefill would blank the modal).
    useEffect(() => {
        if (!isActive) return;
        setName(initial?.audienceName ?? '');
        setNewAttrName('');
        setNewAttrNumeric(false);
        setCatalog([]);
        setOperation(initial?.audienceOperation ?? 'IN');
        if (initial) {
            setPropertyChoice(attrOptions.find(o => o.value === initial.attributeId) ?? null);
        } else {
            setValues([]);
            setOriginalValueIds([]);
            setPropertyChoice(preselectedAttrId ? (attrOptions.find(o => o.value === preselectedAttrId) ?? null) : null);
        }
    }, [isActive]); // eslint-disable-line react-hooks/exhaustive-deps

    const isNew = propertyChoice?.value === NEW_PROPERTY;
    const existingAttr = !isNew ? propertyChoice : null;
    const numeric = isNew ? newAttrNumeric : isNumericAttr(existingAttr?.allowOperation);
    const attributeName = isNew ? (newAttrName || 'this attribute') : (existingAttr?.label ?? 'this attribute');

    // Load catalog + (for edit) current values whenever the chosen existing attribute changes
    const attrId = existingAttr?.value;
    useEffect(() => {
        if (!isActive || isNew || !attrId) { setCatalog([]); return; }
        setLoadingCatalog(true);
        execute('GET', thor_api_urls.attributeValuesByAttribute(attrId))
            .then(d => setCatalog((d?.attributeValues ?? d ?? []).map(v => ({ attributeValueId: v.attributeValueId, value: v.value }))))
            .catch(() => setCatalog([]))
            .finally(() => setLoadingCatalog(false));
    }, [isActive, isNew, attrId, execute]);

    // For edit: prefill the audience's current values once
    const prefilledFor = useRef(null);
    useEffect(() => {
        if (!isActive || !isEdit || !initial?.audienceId) return;
        if (prefilledFor.current === initial.audienceId) return;
        prefilledFor.current = initial.audienceId;
        execute('GET', thor_api_urls.audienceValuesByAudience(initial.audienceId))
            .then(d => {
                const vals = (d?.audienceValues ?? d ?? []).map(v => ({ value: v.value, attributeValueId: v.attributeValueId, isNew: false }));
                setValues(vals);
                setOriginalValueIds(vals.map(v => v.attributeValueId).filter(id => id != null));
            })
            .catch(() => {});
    }, [isActive, isEdit, initial, execute]);
    useEffect(() => { if (!isActive) prefilledFor.current = null; }, [isActive]);

    // All match types are offered regardless of the property's declared value type:
    // the backend doesn't enforce it, and range audiences legitimately exist on
    // properties flagged as text. The chosen match type is the source of truth.
    const opOptions = MATCH_TYPE_OPTIONS;
    const valueCountHint = valueCountIssue(operation, values.length);

    const propertySelectOptions = [{ value: NEW_PROPERTY, label: '+ Create a new attribute' }, ...attrOptions];

    const canSubmit =
        !!name.trim() &&
        (isNew ? !!newAttrName.trim() : !!existingAttr) &&
        !isSubmitting;

    const handleSubmit = () => {
        onSubmit({
            isEdit,
            audienceId: initial?.audienceId,
            audienceName: name.trim(),
            operation,
            property: isNew
                ? { mode: 'new', attributeName: newAttrName.trim(), allowOperation: newAttrNumeric }
                : { mode: 'existing', attributeId: existingAttr.value },
            values,
            originalValueIds,
        });
    };

    const previewSentence = describeAudience({ attributeName, operation, values: values.map(v => v.value) });

    return (
        <div className={`modal ${isActive ? 'is-active' : ''}`}>
            <div className="modal-background" onClick={onClose} />
            <div className="modal-card" style={{ maxWidth: 560 }}>
                <header className="modal-card-head">
                    <p className="modal-card-title">{isEdit ? 'Edit audience' : 'New audience'}</p>
                    <button className="delete" onClick={onClose} />
                </header>
                <section className="modal-card-body">
                    {/* 1 — name */}
                    <div className="field">
                        <label className="label is-small">1 · Name this audience <span className="has-text-danger">*</span></label>
                        <div className="control">
                            <input className="input" value={name} onChange={e => setName(e.target.value)}
                                placeholder="e.g. New app versions" />
                        </div>
                    </div>

                    {/* 2 — target property */}
                    <div className="field">
                        <label className="label is-small">
                            2 · What do you want to target?
                            <span className="has-text-grey has-text-weight-normal ml-2" style={{ cursor: 'help' }}
                                title="A user dimension like locale, device type, or app version — also called a user property.">
                                <FontAwesomeIcon icon={faCircleInfo} /> an attribute
                            </span>
                        </label>
                        <Select
                            options={propertySelectOptions}
                            value={propertyChoice}
                            onChange={opt => { setPropertyChoice(opt); setValues([]); setOperation('IN'); }}
                            placeholder="Choose an attribute or create one…"
                            isSearchable isDisabled={isEdit}
                            formatOptionLabel={(opt, { context }) =>
                                context === 'menu' && opt.value !== NEW_PROPERTY ? (
                                    <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                                        <span>{opt.label}</span>
                                        <span style={{ fontSize: '0.72rem', color: '#aaa' }}>
                                            {opt.allowOperation ? 'numbers / versions' : 'text / categories'}
                                        </span>
                                    </span>
                                ) : opt.label}
                            menuPortalTarget={document.body} menuPosition="fixed"
                            styles={{ menuPortal: b => ({ ...b, zIndex: 9999 }) }} />

                        {isEdit && (
                            <p className="help has-text-grey">An attribute can't be changed after the audience is created.</p>
                        )}

                        {isNew && (
                            <div style={{ marginTop: 10, borderLeft: '3px solid #3273dc', background: '#f5f8ff', borderRadius: '0 6px 6px 0', padding: 12 }}>
                                <div className="field">
                                    <label className="label is-small">Attribute name <span className="has-text-danger">*</span></label>
                                    <div className="control">
                                        <input className="input is-small" value={newAttrName}
                                            onChange={e => setNewAttrName(e.target.value)}
                                            placeholder="e.g. app_version, locale" />
                                    </div>
                                </div>
                                <div className="field mb-0">
                                    <label className="label is-small">What kind of values?</label>
                                    <label className="radio" style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                        <input type="radio" name="vt" checked={!newAttrNumeric} onChange={() => setNewAttrNumeric(false)} />
                                        <span>Text or categories <span className="has-text-grey is-size-7">(country, device, locale)</span></span>
                                    </label>
                                    <label className="radio" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <input type="radio" name="vt" checked={newAttrNumeric} onChange={() => setNewAttrNumeric(true)} />
                                        <span>Numbers or versions <span className="has-text-grey is-size-7">(enables “at least”, “less than”, “between”)</span></span>
                                    </label>
                                </div>
                            </div>
                        )}

                        {existingAttr && (
                            <p className="help has-text-grey" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <span>{existingAttr.label} · {numeric ? 'numbers / versions' : 'text / categories'} · {loadingCatalog ? '…' : `${catalog.length} known value${catalog.length === 1 ? '' : 's'}`}</span>
                                {onManageProperty && (
                                    <button type="button" className="button is-ghost is-small p-0 has-text-link"
                                        onClick={() => onManageProperty(existingAttr.value)} style={{ height: 'auto' }}>
                                        Manage attribute <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: 4 }} />
                                    </button>
                                )}
                            </p>
                        )}
                    </div>

                    {/* 3 — match type */}
                    {propertyChoice && (
                        <div className="field">
                            <label className="label is-small">3 · How should it match?</label>
                            <Select
                                options={opOptions}
                                value={opOptions.find(o => o.value === operation) ?? opOptions[0]}
                                onChange={opt => setOperation(opt?.value ?? 'IN')}
                                menuPortalTarget={document.body} menuPosition="fixed"
                                styles={{ menuPortal: b => ({ ...b, zIndex: 9999 }) }} />
                            <p className="help has-text-grey">Use “is any of” for lists (locale, device); the others suit numbers or versions.</p>
                        </div>
                    )}

                    {/* 4 — values */}
                    {propertyChoice && (
                        <div className="field">
                            <label className="label is-small">
                                4 · {operation === 'BTWN' ? 'Lower and upper values' : RANGE_OPERATIONS.includes(operation) ? 'Threshold value' : 'Values that match'}
                            </label>
                            <ValuePicker catalog={catalog} value={values} onChange={setValues} loading={loadingCatalog} />
                            {valueCountHint && (
                                <p className="help has-text-danger">
                                    <FontAwesomeIcon icon={faTriangleExclamation} /> {valueCountHint}
                                </p>
                            )}
                        </div>
                    )}

                    {/* live preview */}
                    {propertyChoice && (
                        <div style={{ background: '#f6f7f9', borderRadius: 6, padding: '10px 12px', marginTop: 4 }}>
                            <p className="is-size-7 has-text-grey mb-1">Preview</p>
                            <p className="is-size-6">{previewSentence}</p>
                        </div>
                    )}
                </section>
                <footer className="modal-card-foot">
                    <Button className="is-link" onClick={handleSubmit} disabled={!canSubmit}>
                        {isSubmitting ? 'Saving…' : isEdit ? 'Save changes' : 'Create audience'}
                    </Button>
                    <Button onClick={onClose}>Cancel</Button>
                </footer>
            </div>
        </div>
    );
};

// ── Property (attribute) create/edit modal — used by the Properties tab ──────

const AttributeModal = ({ isActive, initial, onClose, onSubmit, isSubmitting }) => {
    const [name, setName]     = useState('');
    const [numeric, setNumeric] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        if (isActive) {
            setName(initial?.attributeName ?? '');
            setNumeric(initial?.allowOperation ?? false);
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [isActive]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className={`modal ${isActive ? 'is-active' : ''}`}>
            <div className="modal-background" onClick={onClose} />
            <div className="modal-card" style={{ maxWidth: 480 }}>
                <header className="modal-card-head">
                    <p className="modal-card-title">{initial ? 'Edit attribute' : 'New attribute'}</p>
                    <button className="delete" onClick={onClose} />
                </header>
                <section className="modal-card-body">
                    <div className="field">
                        <label className="label">Name <span className="has-text-danger">*</span></label>
                        <div className="control">
                            <input ref={inputRef} className="input" value={name}
                                onChange={e => setName(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && name.trim() && onSubmit({ attributeName: name, allowOperation: numeric })}
                                placeholder="e.g. locale, deviceType, app_version" />
                        </div>
                    </div>
                    <div className="field">
                        <label className="label is-small">What kind of values?</label>
                        <label className="radio" style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                            <input type="radio" name="attr-vt" checked={!numeric} onChange={() => setNumeric(false)} />
                            <span>Text or categories <span className="has-text-grey is-size-7">(country, device, locale)</span></span>
                        </label>
                        <label className="radio" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <input type="radio" name="attr-vt" checked={numeric} onChange={() => setNumeric(true)} />
                            <span>Numbers or versions <span className="has-text-grey is-size-7">(enables “at least”, “less than”, “between”)</span></span>
                        </label>
                    </div>
                </section>
                <footer className="modal-card-foot">
                    <Button className="is-link" onClick={() => onSubmit({ attributeName: name, allowOperation: numeric })}
                        disabled={isSubmitting || !name.trim()}>
                        {isSubmitting ? 'Saving…' : 'Save'}
                    </Button>
                    <Button onClick={onClose}>Cancel</Button>
                </footer>
            </div>
        </div>
    );
};

// ── "Try it out" tester (client-side) ───────────────────────────────────────

const AudienceTester = ({ attributeName, operation, values, isNumeric }) => {
    const [input, setInput] = useState('');
    const result = audienceMatches(operation, values, input, isNumeric);

    return (
        <div style={{ background: '#f6f7f9', borderRadius: 6, padding: '10px 12px', marginTop: 10 }}>
            <p className="is-size-7 has-text-weight-semibold mb-2">
                <FontAwesomeIcon icon={faFlask} className="mr-1" /> Try it out
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span className="is-size-7 has-text-grey">If a user’s {attributeName} is</span>
                <input className="input is-small" style={{ width: 150, fontFamily: 'var(--font-mono, monospace)' }}
                    value={input} onChange={e => setInput(e.target.value)} placeholder="enter a value" />
                {input.trim() === '' ? (
                    <span className="is-size-7 has-text-grey">…</span>
                ) : result === true ? (
                    <span className="is-size-7 has-text-success" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <FontAwesomeIcon icon={faCircleCheck} /> matches this audience
                    </span>
                ) : result === false ? (
                    <span className="is-size-7 has-text-grey" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <FontAwesomeIcon icon={faCircleXmark} /> doesn’t match
                    </span>
                ) : (
                    <span className="is-size-7 has-text-grey">can’t test until values are set</span>
                )}
            </div>
        </div>
    );
};

// ── Audience row (two-line, plain-language) ──────────────────────────────────

const AudienceRow = ({ audience, isNewlyCreated, onCreationAcknowledged, onEdit, onDelete, execute, addToast }) => {
    const [open, setOpen]           = useState(!!isNewlyCreated);
    const [valState, setValState]   = useState(buildLoadingState([]));
    const [deletingId, setDeletingId] = useState(null);
    const [addOpen, setAddOpen]     = useState(false);
    const [isAdding, setIsAdding]   = useState(false);
    const [attrValOptions, setAttrValOptions] = useState([]);
    const [loadingAttrVals, setLoadingAttrVals] = useState(false);
    const [confirmDel, setConfirmDel] = useState(null);
    const loaded = useRef(false);

    const loadValues = useCallback(() => {
        if (loaded.current) return;
        loaded.current = true;
        execute('GET', thor_api_urls.audienceValuesByAudience(audience.audienceId))
            .then(d => setValState(buildDataState(d?.audienceValues ?? d ?? [])))
            .catch(err => setValState(buildErrorState(err, [])));
    }, [execute, audience.audienceId]);

    // Lazy: only fetch this audience's values when the row is expanded — avoids a
    // GET-per-audience flood on a large library. Collapsed rows show the count
    // (which the list API already provides). Re-fetches if the audience changes
    // beneath us while open (e.g. edited via the builder, which refetches the list).
    useEffect(() => {
        if (!open) return;
        loaded.current = false;
        loadValues();
    }, [open, loadValues, audience.audienceValueCount, audience.audienceOperation]);

    const openAddModal = () => {
        setLoadingAttrVals(true);
        setAttrValOptions([]);
        execute('GET', thor_api_urls.attributeValuesByAttribute(audience.attributeId))
            .then(d => setAttrValOptions((d?.attributeValues ?? d ?? []).map(v => ({ value: v.attributeValueId, label: v.value }))))
            .catch(() => setAttrValOptions([]))
            .finally(() => setLoadingAttrVals(false));
        setAddOpen(true);
    };

    const handleAddValue = (attributeValueId) => {
        setIsAdding(true);
        execute('POST', thor_api_urls.audienceValuesBase, { audienceId: audience.audienceId, attributeValueId })
            .then(() => { loaded.current = false; loadValues(); setAddOpen(false); addToast('Value added.', 'success'); })
            .catch(err => addToast(serverErrMsg(err, 'Failed to add value.'), 'danger'))
            .finally(() => setIsAdding(false));
    };

    const handleDeleteValue = (val) => {
        setDeletingId(val.attributeValueId);
        execute('DELETE', thor_api_urls.audienceValuesBase, { audienceId: audience.audienceId, attributeValueId: val.attributeValueId })
            .then(() => {
                setValState(s => buildDataState((s.data ?? []).filter(v => v.attributeValueId !== val.attributeValueId)));
                addToast('Value removed.', 'success');
            })
            .catch(err => addToast(serverErrMsg(err, 'Failed to remove value.'), 'danger'))
            .finally(() => setDeletingId(null));
    };

    const values        = valState.data ?? [];
    const operation     = audience.audienceOperation;
    const numeric       = RANGE_OPERATIONS.includes(operation);
    const locked        = audience.isUsedByExpRunningInProd;
    const inUse         = audience.isUsedByUnarchivedExp;
    const valuesMutable = !locked;
    const valueCount    = audience.audienceValueCount ?? values.length;
    const issue         = valueCountIssue(operation, valueCount);
    const valueStrings  = values.map(v => v.value);

    return (
        <>
            <div style={{ border: '1px solid #e6e9f0', borderRadius: 8, marginBottom: 8, background: open ? '#fafbff' : '#fff' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', padding: '12px 14px', gap: 12 }}>
                    <button className="button is-ghost is-small p-0" style={{ height: 'auto', marginTop: 2 }}
                        onClick={() => setOpen(o => !o)} aria-label={open ? 'Collapse' : 'Expand'}>
                        <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} style={{ color: '#bbb', width: 10 }} />
                    </button>

                    <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
                        {/* line 1 — name + status */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 3 }}>
                            <span style={{ fontWeight: 600 }}>{audience.audienceName}</span>
                            {locked && (
                                <span className="tag is-danger is-light is-small" style={{ cursor: 'help' }}
                                    title="Used by a running production experiment — can't be edited">
                                    <FontAwesomeIcon icon={faLock} className="mr-1" /> in a running experiment
                                </span>
                            )}
                            {inUse && !locked && (
                                <span className="tag is-warning is-light is-small" style={{ cursor: 'help' }}
                                    title="Attached to one or more active experiments — can't be deleted">
                                    in use
                                </span>
                            )}
                            {issue && (
                                <span className="tag is-danger is-light is-small" style={{ cursor: 'help' }} title={issue}>
                                    <FontAwesomeIcon icon={faTriangleExclamation} className="mr-1" /> needs values
                                </span>
                            )}
                        </div>

                        {/* line 2 — the rule, in plain English */}
                        <div className="is-size-7" style={{ color: '#5a5a5a' }}>
                            Matches users whose <span style={{ color: '#222', fontWeight: 500 }}>{audience.attributeName}</span>{' '}
                            {OPERATION_PHRASE[operation] ?? operation}{' '}
                            {issue ? (
                                <span className="has-text-danger">— {issue.toLowerCase()}</span>
                            ) : (
                                <span className="has-text-grey">· {valueCount} {valueCount === 1 ? 'value' : 'values'}</span>
                            )}
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                        <button className="button is-small is-ghost"
                            title={locked ? "Can't edit: used by a running experiment" : 'Edit'}
                            disabled={locked} onClick={() => onEdit(audience)}>
                            <Icon icon={faPencil} />
                        </button>
                        <button className="button is-small is-ghost has-text-danger"
                            title={inUse ? "Can't delete: attached to an active experiment" : 'Delete'}
                            disabled={inUse} onClick={() => onDelete(audience)}>
                            <Icon icon={faTrash} />
                        </button>
                    </div>
                </div>

                {open && (
                    <div style={{ padding: '0 14px 14px 36px', borderTop: '1px solid #eef' }}>
                        {isNewlyCreated && values.length === 0 && !valState.loading && !valState.error && (
                            <div className="notification is-link is-light is-size-7 py-2 px-3 my-3" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>Audience created. Add values below to define which users match.</span>
                                <button className="delete is-small" onClick={onCreationAcknowledged} />
                            </div>
                        )}
                        <p className="is-size-7 has-text-grey mt-3 mb-2">Values</p>
                        {valState.loading && <SmallLoadingSpinner />}
                        {valState.error   && <p className="has-text-danger is-size-7">Failed to load values.</p>}
                        {!valState.loading && !valState.error && (
                            <>
                                <div style={{ display: 'flex', flexWrap: 'wrap', marginBottom: 6 }}>
                                    {values.length === 0
                                        ? <span className="has-text-grey is-size-7 mr-3 mb-1">No values yet.</span>
                                        : values.map(v => (
                                            <ValueChip key={v.attributeValueId} label={v.value}
                                                removing={deletingId === v.attributeValueId}
                                                onRemove={valuesMutable ? () => setConfirmDel(v) : undefined} />
                                        ))
                                    }
                                </div>
                                {valuesMutable && (
                                    <button className="button is-small is-light" onClick={openAddModal}>
                                        <span className="icon is-small"><FontAwesomeIcon icon={faPlus} /></span>
                                        <span>Add value</span>
                                    </button>
                                )}

                                <AudienceTester attributeName={audience.attributeName} operation={operation}
                                    values={valueStrings} isNumeric={numeric} />
                            </>
                        )}
                    </div>
                )}
            </div>

            <AddValueModal isActive={addOpen} title={`Add value — ${audience.audienceName}`}
                selectOptions={attrValOptions} loadingOptions={loadingAttrVals}
                onClose={() => setAddOpen(false)} onSubmit={handleAddValue} isSubmitting={isAdding} />

            <ConfirmDialog isActive={confirmDel !== null} title="Remove value"
                message={`Remove "${confirmDel?.value}" from ${audience.audienceName}?`}
                confirmLabel="Remove" confirmClass="is-danger"
                onConfirm={() => { handleDeleteValue(confirmDel); setConfirmDel(null); }}
                onCancel={() => setConfirmDel(null)} />
        </>
    );
};

// ── Property (attribute) row ─────────────────────────────────────────────────

const AttributeRow = ({ attr, inUse, onEdit, onDelete, onNewAudience, execute, addToast }) => {
    const [open, setOpen]           = useState(false);
    const [valState, setValState]   = useState(buildLoadingState([]));
    const [deletingId, setDeletingId] = useState(null);
    const [addOpen, setAddOpen]     = useState(false);
    const [isAdding, setIsAdding]   = useState(false);
    const [confirmDel, setConfirmDel] = useState(null);
    const loaded = useRef(false);

    const loadValues = useCallback(() => {
        if (loaded.current) return;
        loaded.current = true;
        execute('GET', thor_api_urls.attributeValuesByAttribute(attr.attributeId))
            .then(d => setValState(buildDataState(d?.attributeValues ?? d ?? [])))
            .catch(err => setValState(buildErrorState(err, [])));
    }, [execute, attr.attributeId]);

    const toggle = () => { if (!open) loadValues(); setOpen(o => !o); };

    const handleAddValue = (value) => {
        setIsAdding(true);
        execute('POST', thor_api_urls.attributeValuesCreate, { attributeId: attr.attributeId, value })
            .then(() => { loaded.current = false; loadValues(); setAddOpen(false); addToast('Value added.', 'success'); })
            .catch(err => addToast(serverErrMsg(err, 'Failed to add value.'), 'danger'))
            .finally(() => setIsAdding(false));
    };

    const handleDeleteValue = (val) => {
        setDeletingId(val.attributeValueId);
        execute('DELETE', thor_api_urls.attributeValue(val.attributeValueId))
            .then(() => {
                setValState(s => buildDataState((s.data ?? []).filter(v => v.attributeValueId !== val.attributeValueId)));
                addToast('Value deleted.', 'success');
            })
            .catch(err => addToast(serverErrMsg(err, 'Failed to delete value.'), 'danger'))
            .finally(() => setDeletingId(null));
    };

    const values = valState.data ?? [];

    return (
        <>
            <div style={{ border: '1px solid #e8e8e8', borderRadius: 8, marginBottom: 8, background: open ? '#fafbff' : '#fff' }}>
                <div style={{ display: 'flex', alignItems: 'center', padding: '12px 14px', cursor: 'pointer', userSelect: 'none', gap: 8 }}
                    onClick={toggle}>
                    <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} style={{ color: '#bbb', width: 10, flexShrink: 0 }} />
                    <span style={{ fontWeight: 600 }}>{attr.attributeName}</span>
                    <span className="tag is-light is-small" style={{ flexShrink: 0 }}
                        title={attr.allowOperation ? 'Supports range matches: at least, less than, between' : 'Matches against a list of values'}>
                        {attr.allowOperation ? 'numbers / versions' : 'text / categories'}
                    </span>

                    <div style={{ flex: 1 }} />
                    <div onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: 2 }}>
                        <button className="button is-small is-ghost has-text-link" title="Add a value"
                            onClick={() => { loadValues(); setOpen(true); setAddOpen(true); }}>
                            <Icon icon={faPlus} />
                        </button>
                        <button className="button is-small is-ghost" title="New audience from this attribute"
                            onClick={() => onNewAudience(attr.attributeId)}>
                            <Icon icon={faUsers} />
                        </button>
                        <button className="button is-small is-ghost" title="Edit" onClick={() => onEdit(attr)}>
                            <Icon icon={faPencil} />
                        </button>
                        <button className="button is-small is-ghost has-text-danger"
                            title={inUse ? "Can't delete: used by audiences" : 'Delete'}
                            disabled={inUse} onClick={() => onDelete(attr)}>
                            <Icon icon={faTrash} />
                        </button>
                    </div>
                </div>

                {open && (
                    <div style={{ padding: '8px 14px 12px 30px', borderTop: '1px solid #eee' }}>
                        {valState.loading && <SmallLoadingSpinner />}
                        {valState.error   && <p className="has-text-danger is-size-7">Failed to load values.</p>}
                        {!valState.loading && !valState.error && (
                            <>
                                <div style={{ display: 'flex', flexWrap: 'wrap', marginBottom: 6 }}>
                                    {values.length === 0
                                        ? <span className="has-text-grey is-size-7 mr-3 mb-1">No values yet.</span>
                                        : values.map(v => (
                                            <ValueChip key={v.attributeValueId} label={v.value}
                                                removing={deletingId === v.attributeValueId}
                                                onRemove={inUse ? undefined : () => setConfirmDel(v)} />
                                        ))
                                    }
                                </div>
                                <button className="button is-small is-light" onClick={() => setAddOpen(true)}>
                                    <span className="icon is-small"><FontAwesomeIcon icon={faPlus} /></span>
                                    <span>Add value</span>
                                </button>
                            </>
                        )}
                    </div>
                )}
            </div>

            <AddValueModal isActive={addOpen} freeText title={`Add value — ${attr.attributeName}`}
                onClose={() => setAddOpen(false)} onSubmit={handleAddValue} isSubmitting={isAdding} />

            <ConfirmDialog isActive={confirmDel !== null} title="Delete value"
                message={`Delete value "${confirmDel?.value}"?`}
                confirmLabel="Delete" confirmClass="is-danger"
                onConfirm={() => { handleDeleteValue(confirmDel); setConfirmDel(null); }}
                onCancel={() => setConfirmDel(null)} />
        </>
    );
};

// ── Main page ────────────────────────────────────────────────────────────────

const AudiencesManagerPage = () => {
    const { execute } = useFetchWithMsal();
    const { addToast } = useThor();
    const location = useLocation();

    const initialQ = new URLSearchParams(location.search).get('q') ?? '';
    const [activeTab, setActiveTab] = useState('audiences');
    const [audienceSearch, setAudienceSearch] = useState(initialQ);
    const [attributeSearch, setAttributeSearch] = useState('');
    const [newlyCreatedId, setNewlyCreatedId] = useState(null);
    const [sortBy, setSortBy]         = useState('name');
    const [filterAttrId, setFilterAttrId] = useState(null);

    const [attributesState, setAttributesState]     = useState(buildLoadingState([]));
    const [attrModal, setAttrModal]                 = useState({ open: false, initial: null });
    const [isSubmittingAttr, setIsSubmittingAttr]   = useState(false);
    const [confirmDeleteAttr, setConfirmDeleteAttr] = useState(null);

    const [audiencesState, setAudiencesState]               = useState(buildLoadingState([]));
    const [audienceModal, setAudienceModal]                 = useState({ open: false, initial: null, preselectedAttrId: null });
    const [isSubmittingAudience, setIsSubmittingAudience]   = useState(false);
    const [confirmDeleteAudience, setConfirmDeleteAudience] = useState(null);

    const fetchAttributes = useCallback(() => {
        setAttributesState(buildLoadingState([]));
        execute('GET', thor_api_urls.attributes)
            .then(d => setAttributesState(buildDataState(d?.attributes ?? d ?? [])))
            .catch(err => setAttributesState(buildErrorState(err, [])));
    }, [execute]);

    const fetchAudiences = useCallback(() => {
        setAudiencesState(buildLoadingState([]));
        execute('GET', thor_api_urls.audienceAttributes)
            .then(d => setAudiencesState(buildDataState(d?.audienceAttributes ?? d ?? [])))
            .catch(err => setAudiencesState(buildErrorState(err, [])));
    }, [execute]);

    useEffect(() => {
        fetchAttributes();
        fetchAudiences();
    }, [fetchAttributes, fetchAudiences]);

    const attributes = attributesState.data ?? [];
    const audiences  = audiencesState.data ?? [];
    const usedAttrIds = new Set(audiences.map(a => a.attributeId));

    const aq = audienceSearch.trim().toLowerCase();
    const atq = attributeSearch.trim().toLowerCase();

    const isAudienceBroken = (a) => !!valueCountIssue(a.audienceOperation, a.audienceValueCount ?? 0);

    let filteredAudiences = aq ? audiences.filter(a =>
        a.audienceName.toLowerCase().includes(aq) || (a.attributeName ?? '').toLowerCase().includes(aq)
    ) : audiences;

    if (filterAttrId) filteredAudiences = filteredAudiences.filter(a => a.attributeId === filterAttrId);

    if (sortBy === 'name') {
        filteredAudiences = [...filteredAudiences].sort((a, b) => a.audienceName.localeCompare(b.audienceName));
    } else if (sortBy === 'attribute') {
        filteredAudiences = [...filteredAudiences].sort((a, b) =>
            (a.attributeName ?? '').localeCompare(b.attributeName ?? '') || a.audienceName.localeCompare(b.audienceName)
        );
    } else if (sortBy === 'warnings') {
        filteredAudiences = [...filteredAudiences].sort((a, b) =>
            (isAudienceBroken(a) ? 0 : 1) - (isAudienceBroken(b) ? 0 : 1) || a.audienceName.localeCompare(b.audienceName)
        );
    }

    const filteredAttributes = atq ? attributes.filter(a => a.attributeName.toLowerCase().includes(atq)) : attributes;

    // ── Property CRUD ──────────────────────────────────────────────────────────
    const handleAttrSubmit = (payload) => {
        setIsSubmittingAttr(true);
        const isEdit = !!attrModal.initial;
        const req = isEdit
            ? execute('PUT', thor_api_urls.attribute(attrModal.initial.attributeId), payload)
            : execute('POST', thor_api_urls.attributes, payload);
        req
            .then(() => { setAttrModal({ open: false, initial: null }); fetchAttributes(); addToast(`Attribute ${isEdit ? 'updated' : 'created'}.`, 'success'); })
            .catch(err => addToast(serverErrMsg(err, `Failed to ${isEdit ? 'update' : 'create'} attribute.`), 'danger'))
            .finally(() => setIsSubmittingAttr(false));
    };

    const handleDeleteAttr = (attr) => {
        execute('DELETE', thor_api_urls.attribute(attr.attributeId))
            .then(() => { fetchAttributes(); addToast('Attribute deleted.', 'success'); })
            .catch(err => addToast(serverErrMsg(err, 'Failed to delete attribute.'), 'danger'));
    };

    // ── Audience builder submit (unified create / edit, multi-step) ─────────────
    const handleBuilderSubmit = async (payload) => {
        setIsSubmittingAudience(true);
        try {
            // 1. create the property if it's brand new
            let attributeId = payload.property.attributeId;
            if (payload.property.mode === 'new') {
                const attr = await execute('POST', thor_api_urls.attributes, {
                    attributeName: payload.property.attributeName,
                    allowOperation: payload.property.allowOperation,
                });
                attributeId = attr?.attributeId ?? attr?.attribute?.attributeId;
                if (attributeId == null) throw new Error('Could not create attribute');
            }

            // 2. resolve every value to an attributeValueId (creating new ones first)
            const resolvedIds = [];
            for (const v of payload.values) {
                if (v.attributeValueId != null) {
                    resolvedIds.push(v.attributeValueId);
                } else {
                    const created = await execute('POST', thor_api_urls.attributeValuesCreate, { attributeId, value: v.value });
                    const id = created?.attributeValueId ?? created?.attributeValue?.attributeValueId;
                    if (id == null) throw new Error(`Could not save value "${v.value}"`);
                    resolvedIds.push(id);
                }
            }

            // 3. create or update the audience itself
            let audienceId = payload.audienceId;
            if (payload.isEdit) {
                await execute('PUT', thor_api_urls.audience(audienceId), {
                    audienceName: payload.audienceName, attributeId, operation: payload.operation,
                });
            } else {
                const aud = await execute('POST', thor_api_urls.audiences, {
                    audienceName: payload.audienceName, attributeId, operation: payload.operation,
                });
                audienceId = aud?.audienceId ?? aud?.audience?.audienceId;
                if (audienceId == null) throw new Error('Could not create audience');
            }

            // 4. diff audience values: add the new ones, remove the dropped ones
            const want = new Set(resolvedIds);
            const orig = new Set(payload.originalValueIds ?? []);
            for (const id of want) {
                if (!orig.has(id)) await execute('POST', thor_api_urls.audienceValuesBase, { audienceId, attributeValueId: id });
            }
            for (const id of orig) {
                if (!want.has(id)) await execute('DELETE', thor_api_urls.audienceValuesBase, { audienceId, attributeValueId: id });
            }

            setAudienceModal({ open: false, initial: null, preselectedAttrId: null });
            if (!payload.isEdit) setNewlyCreatedId(audienceId);
            addToast(`Audience ${payload.isEdit ? 'updated' : 'created'}.`, 'success');
            fetchAudiences();
            fetchAttributes();
        } catch (err) {
            addToast(serverErrMsg(err, `Failed to ${payload.isEdit ? 'update' : 'create'} audience.`), 'danger');
            // A mid-sequence failure may have already created a property/audience;
            // refetch so the lists reflect whatever actually persisted.
            fetchAudiences();
            fetchAttributes();
        } finally {
            setIsSubmittingAudience(false);
        }
    };

    const handleDeleteAudience = (audience) => {
        execute('DELETE', thor_api_urls.audience(audience.audienceId))
            .then(() => { fetchAudiences(); addToast('Audience deleted.', 'success'); })
            .catch(err => addToast(serverErrMsg(err, 'Failed to delete audience.'), 'danger'));
    };

    const openManageProperty = (attrId) => {
        setAudienceModal({ open: false, initial: null, preselectedAttrId: null });
        setActiveTab('attributes');
        setAttributeSearch('');
        setFilterAttrId(null);
        const attr = attributes.find(a => a.attributeId === attrId);
        if (attr) setAttributeSearch(attr.attributeName);
    };

    return (
        <>
            {/* ── Tabs + primary action ─────────────────────────────────────── */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #e8e8e8', marginBottom: 16 }}>
                <div style={{ display: 'flex', gap: 0 }}>
                    {[
                        { key: 'audiences', label: `Audiences (${audiences.length})` },
                        { key: 'attributes', label: `Attributes (${attributes.length})` },
                    ].map(({ key, label }) => (
                        <button key={key} onClick={() => setActiveTab(key)}
                            style={{
                                background: 'none', border: 'none', cursor: 'pointer',
                                padding: '8px 18px', fontWeight: activeTab === key ? 700 : 400,
                                color: activeTab === key ? '#3273dc' : '#555',
                                borderBottom: activeTab === key ? '2px solid #3273dc' : '2px solid transparent',
                                marginBottom: -2, fontSize: '0.95rem',
                            }}>
                            {label}
                        </button>
                    ))}
                </div>
                {activeTab === 'audiences' ? (
                    <Button className="is-link is-small"
                        onClick={() => setAudienceModal({ open: true, initial: null, preselectedAttrId: null })}>
                        <FontAwesomeIcon icon={faPlus} style={{ marginRight: 6 }} /> New audience
                    </Button>
                ) : (
                    <Button className="is-light is-small" onClick={() => setAttrModal({ open: true, initial: null })}>
                        <FontAwesomeIcon icon={faPlus} style={{ marginRight: 6 }} /> New attribute
                    </Button>
                )}
            </div>

            {activeTab === 'attributes' && (
                <p className="is-size-7 has-text-grey mb-3">
                    Attributes (also called user properties) are the user dimensions audiences are built on — like locale, device type, or app version.
                </p>
            )}

            {/* ── Search + filters ──────────────────────────────────────────── */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <div className="control has-icons-left" style={{ flex: 1, minWidth: 180 }}>
                    <input className="input is-small"
                        placeholder={activeTab === 'audiences' ? 'Search audiences or attributes…' : 'Search attributes…'}
                        value={activeTab === 'audiences' ? audienceSearch : attributeSearch}
                        onChange={e => activeTab === 'audiences' ? setAudienceSearch(e.target.value) : setAttributeSearch(e.target.value)} />
                    <span className="icon is-left is-small"><FontAwesomeIcon icon={faMagnifyingGlass} /></span>
                </div>

                {activeTab === 'audiences' && (
                    <>
                        <div className="select is-small">
                            <select value={filterAttrId ?? ''} onChange={e => setFilterAttrId(e.target.value ? Number(e.target.value) : null)}>
                                <option value="">All attributes</option>
                                {attributes.map(a => (
                                    <option key={a.attributeId} value={a.attributeId}>{a.attributeName}</option>
                                ))}
                            </select>
                        </div>
                        <div className="select is-small">
                            <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
                                <option value="name">Sort: Name</option>
                                <option value="attribute">Sort: Attribute</option>
                                <option value="warnings">Sort: Needs attention first</option>
                            </select>
                        </div>
                    </>
                )}

                {(activeTab === 'audiences' ? audienceSearch || filterAttrId : attributeSearch) && (
                    <span className="has-text-grey is-size-7" style={{ whiteSpace: 'nowrap' }}>
                        {activeTab === 'audiences'
                            ? `${filteredAudiences.length} / ${audiences.length}`
                            : `${filteredAttributes.length} / ${attributes.length}`}
                    </span>
                )}
            </div>

            {/* ── Audiences ─────────────────────────────────────────────────── */}
            {activeTab === 'audiences' && (
                <StateHandler showLoader={audiencesState.loading} showError={audiencesState.error}
                    loadingScreen={<SmallLoadingSpinner />}>
                    {filteredAudiences.length === 0 ? (
                        <p className="has-text-grey">{audienceSearch || filterAttrId ? 'No audiences match your filters.' : 'No audiences yet.'}</p>
                    ) : (
                        filteredAudiences.map(a => (
                            <AudienceRow key={a.audienceId} audience={a}
                                isNewlyCreated={a.audienceId === newlyCreatedId}
                                onCreationAcknowledged={() => setNewlyCreatedId(null)}
                                onEdit={aud => setAudienceModal({ open: true, initial: aud, preselectedAttrId: null })}
                                onDelete={aud => setConfirmDeleteAudience(aud)}
                                execute={execute} addToast={addToast} />
                        ))
                    )}
                </StateHandler>
            )}

            {/* ── Properties ────────────────────────────────────────────────── */}
            {activeTab === 'attributes' && (
                <StateHandler showLoader={attributesState.loading} showError={attributesState.error}
                    loadingScreen={<SmallLoadingSpinner />}>
                    {filteredAttributes.length === 0 ? (
                        <p className="has-text-grey">{attributeSearch ? 'No attributes match your search.' : 'No attributes yet.'}</p>
                    ) : (
                        filteredAttributes.map(attr => (
                            <AttributeRow key={attr.attributeId} attr={attr}
                                inUse={usedAttrIds.has(attr.attributeId)}
                                onEdit={a => setAttrModal({ open: true, initial: a })}
                                onDelete={a => setConfirmDeleteAttr(a)}
                                onNewAudience={attrId => { setActiveTab('audiences'); setAudienceModal({ open: true, initial: null, preselectedAttrId: attrId }); }}
                                execute={execute} addToast={addToast} />
                        ))
                    )}
                </StateHandler>
            )}

            {/* ── Modals ────────────────────────────────────────────────────── */}
            <AudienceBuilderModal isActive={audienceModal.open} initial={audienceModal.initial}
                attributes={attributes} preselectedAttrId={audienceModal.preselectedAttrId} execute={execute}
                onClose={() => setAudienceModal({ open: false, initial: null, preselectedAttrId: null })}
                onSubmit={handleBuilderSubmit} isSubmitting={isSubmittingAudience}
                onManageProperty={openManageProperty} />

            <AttributeModal isActive={attrModal.open} initial={attrModal.initial}
                onClose={() => setAttrModal({ open: false, initial: null })}
                onSubmit={handleAttrSubmit} isSubmitting={isSubmittingAttr} />

            <ConfirmDialog isActive={confirmDeleteAttr !== null} title="Delete attribute"
                message={`Delete attribute "${confirmDeleteAttr?.attributeName}"? This cannot be undone.`}
                confirmLabel="Delete" confirmClass="is-danger"
                onConfirm={() => { handleDeleteAttr(confirmDeleteAttr); setConfirmDeleteAttr(null); }}
                onCancel={() => setConfirmDeleteAttr(null)} />

            <ConfirmDialog isActive={confirmDeleteAudience !== null} title="Delete audience"
                message={`Delete audience "${confirmDeleteAudience?.audienceName}"? This cannot be undone.`}
                confirmLabel="Delete" confirmClass="is-danger"
                onConfirm={() => { handleDeleteAudience(confirmDeleteAudience); setConfirmDeleteAudience(null); }}
                onCancel={() => setConfirmDeleteAudience(null)} />
        </>
    );
};

export default AudiencesManagerPage;
